import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '../cn.js';

/**
 * Form controls.
 *
 * Every control is wired to its label, description and error through
 * `aria-describedby` and `aria-invalid`. Several of these fields -- notably
 * the justification on every submission -- become audit evidence, so the
 * validation state has to be perceivable without relying on colour alone.
 */

const CONTROL_BASE =
  'w-full rounded-lg border bg-sunken text-body placeholder:text-subtle ' +
  'transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

const CONTROL_OK = 'border-line-strong hover:border-brand/60';
const CONTROL_ERROR = 'border-danger-border';

/**
 * How much room a field takes.
 *
 * `default` is every form in the tool: a page of stacked fields, where the
 * label wants to be as readable as the value. `compact` is for a console
 * header, where the fields sit in one row above a work area and every pixel
 * they take is a pixel of editor -- so the label shrinks to a caption and
 * the control loses its breathing room.
 *
 * A prop rather than two components because the wiring underneath -- the
 * label association, `aria-describedby`, `aria-invalid` -- is the part worth
 * having exactly once, and it is the part a hand-rolled compact field would
 * get wrong.
 */
export type FieldDensity = 'default' | 'compact';

const SHELL_DENSITY: Record<FieldDensity, string> = {
  default: 'gap-1.5',
  compact: 'gap-0.5',
};

const LABEL_DENSITY: Record<FieldDensity, string> = {
  default: 'text-sm font-medium text-body',
  compact: 'text-[0.7rem] font-medium uppercase tracking-wide text-subtle',
};

const HINT_DENSITY: Record<FieldDensity, string> = {
  default: 'text-xs',
  compact: 'text-[0.65rem]',
};

const CONTROL_DENSITY: Record<FieldDensity, string> = {
  default: 'px-3 py-1.5 text-sm',
  compact: 'px-2 py-1 text-xs',
};

interface FieldShellProps {
  label: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** Right-aligned hint, typically a character counter. */
  hint?: ReactNode;
  density?: FieldDensity;
  children: (ids: { controlId: string; describedBy: string | undefined }) => ReactNode;
  className?: string;
}

export function FieldShell({
  label,
  description,
  error,
  required,
  hint,
  density = 'default',
  children,
  className,
}: FieldShellProps) {
  const base = useId();
  const controlId = `${base}-control`;
  const descriptionId = description ? `${base}-description` : undefined;
  const errorId = error ? `${base}-error` : undefined;
  const describedBy = [descriptionId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('flex flex-col', SHELL_DENSITY[density], className)}>
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={controlId} className={cn('truncate', LABEL_DENSITY[density])}>
          {label}
          {required ? (
            <span className="text-danger-fg ml-0.5" aria-hidden="true">
              *
            </span>
          ) : null}
        </label>
        {hint ? (
          <span className={cn('truncate text-subtle tabular-nums', HINT_DENSITY[density])}>
            {hint}
          </span>
        ) : null}
      </div>

      {description ? (
        <p id={descriptionId} className={cn('text-muted', HINT_DENSITY[density])}>
          {description}
        </p>
      ) : null}

      {children({ controlId, describedBy })}

      {error ? (
        <p id={errorId} className={cn('text-danger-fg', HINT_DENSITY[density])} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  hint?: ReactNode;
  density?: FieldDensity;
  containerClassName?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  {
    label,
    description,
    error,
    hint,
    density = 'default',
    containerClassName,
    className,
    required,
    ...rest
  },
  ref,
) {
  return (
    <FieldShell
      label={label}
      description={description}
      error={error}
      hint={hint}
      required={required}
      density={density}
      className={containerClassName}
    >
      {({ controlId, describedBy }) => (
        <input
          ref={ref}
          id={controlId}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          required={required}
          className={cn(
            CONTROL_BASE,
            CONTROL_DENSITY[density],
            error ? CONTROL_ERROR : CONTROL_OK,
            className,
          )}
          {...rest}
        />
      )}
    </FieldShell>
  );
});

export interface TextAreaFieldProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'id'
> {
  label: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  hint?: ReactNode;
  containerClassName?: string;
}

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  function TextAreaField(
    { label, description, error, hint, containerClassName, className, required, ...rest },
    ref,
  ) {
    return (
      <FieldShell
        label={label}
        description={description}
        error={error}
        hint={hint}
        required={required}
        className={containerClassName}
      >
        {({ controlId, describedBy }) => (
          <textarea
            ref={ref}
            id={controlId}
            aria-describedby={describedBy}
            aria-invalid={error ? true : undefined}
            required={required}
            className={cn(
              CONTROL_BASE,
              CONTROL_DENSITY.default,
              'resize-y',
              error ? CONTROL_ERROR : CONTROL_OK,
              className,
            )}
            {...rest}
          />
        )}
      </FieldShell>
    );
  },
);

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: ReactNode;
  options: readonly SelectOption[];
  description?: ReactNode;
  error?: ReactNode;
  placeholder?: string;
  density?: FieldDensity;
  containerClassName?: string;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  {
    label,
    options,
    description,
    error,
    placeholder,
    density = 'default',
    containerClassName,
    className,
    required,
    ...rest
  },
  ref,
) {
  return (
    <FieldShell
      label={label}
      description={description}
      error={error}
      required={required}
      density={density}
      className={containerClassName}
    >
      {({ controlId, describedBy }) => (
        <select
          ref={ref}
          id={controlId}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          required={required}
          className={cn(
            CONTROL_BASE,
            CONTROL_DENSITY[density],
            error ? CONTROL_ERROR : CONTROL_OK,
            className,
          )}
          {...rest}
        >
          {placeholder ? (
            <option value="" disabled>
              {placeholder}
            </option>
          ) : null}
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </FieldShell>
  );
});

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'id'> {
  label: ReactNode;
  description?: ReactNode;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, className, ...rest },
  ref,
) {
  const base = useId();
  const id = `${base}-checkbox`;
  const descriptionId = description ? `${base}-description` : undefined;

  return (
    <div className="flex items-start gap-2.5">
      <input
        ref={ref}
        id={id}
        type="checkbox"
        aria-describedby={descriptionId}
        className={cn(
          'mt-0.5 h-4 w-4 shrink-0 rounded border-line-strong bg-sunken accent-brand',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          className,
        )}
        {...rest}
      />
      <div className="min-w-0">
        <label htmlFor={id} className="text-sm text-body">
          {label}
        </label>
        {description ? (
          <p id={descriptionId} className="text-xs text-muted">
            {description}
          </p>
        ) : null}
      </div>
    </div>
  );
});
